import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingSpinnerProps {
  /** Primary message shown below the spinner */
  message?: string;
  /** Secondary message shown in smaller text */
  detail?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to take full page height */
  fullPage?: boolean;
  /** Optional progress percentage (0-100) */
  progress?: number;
  /** Optional step indicators */
  steps?: { label: string; complete: boolean; active: boolean }[];
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  detail,
  size = 'md',
  fullPage = false,
  progress,
  steps
}) => {
  const spinnerSize = {
    sm: 'h-5 w-5',
    md: 'h-8 w-8',
    lg: 'h-10 w-10'
  }[size];

  const textSize = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  }[size];

  return (
    <div className={`flex items-center justify-center ${fullPage ? 'min-h-screen bg-gray-50' : 'py-12'}`}>
      <div className="text-center max-w-sm mx-auto px-4">
        <Loader2 className={`${spinnerSize} animate-spin mx-auto mb-4 text-gray-400`} />

        {message && (
          <p className={`${textSize} font-medium text-gray-900 mb-1`}>{message}</p>
        )}

        {detail && (
          <p className="text-sm text-gray-500">{detail}</p>
        )}

        {/* Progress bar */}
        {progress !== undefined && (
          <div className="mt-4 w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-gray-900 h-1.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}

        {/* Step indicators */}
        {steps && steps.length > 0 && (
          <div className="mt-4 space-y-2">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {step.complete ? (
                  <div className="h-4 w-4 rounded-full bg-gray-900 flex items-center justify-center">
                    <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : step.active ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-900" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                )}
                <span className={step.active ? 'text-gray-900 font-medium' : step.complete ? 'text-gray-500' : 'text-gray-400'}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Processing overlay for long-running operations.
 * Shows a centered modal-like treatment over the page content.
 */
interface ProcessingOverlayProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Primary message */
  message: string;
  /** Secondary detail */
  detail?: string;
  /** Step indicators */
  steps?: { label: string; complete: boolean; active: boolean }[];
}

export const ProcessingOverlay: React.FC<ProcessingOverlayProps> = ({
  visible,
  message,
  detail,
  steps
}) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-sm w-full mx-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
        <p className="text-base font-medium text-gray-900 text-center mb-1">{message}</p>
        {detail && (
          <p className="text-sm text-gray-500 text-center">{detail}</p>
        )}
        {steps && steps.length > 0 && (
          <div className="mt-5 space-y-2.5">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm">
                {step.complete ? (
                  <div className="h-5 w-5 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : step.active ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-900 flex-shrink-0" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-gray-200 flex-shrink-0" />
                )}
                <span className={step.active ? 'text-gray-900 font-medium' : step.complete ? 'text-gray-500' : 'text-gray-400'}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
